import { SearchTags } from '@/components/search/search-tags';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
} from '@/components/ui/sidebar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { bookDb } from '@/db/book';
import type { Book, Tag } from '@/db/schema';
import { tagsDb } from '@/db/tags';
import { useLiveQuery } from 'dexie-react-hooks';
import { BookOpen, TagsIcon, Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';

const clearDatabase = async () => {
    await tagsDb.tags.clear();
    await bookDb.books.clear();
    localStorage.removeItem('seedVersion');
    localStorage.removeItem('bookSeedVersion');
    localStorage.removeItem('snippetIndexSeedVersion');
    localStorage.removeItem('relationshipTagSeedVersion');
    window.location.reload();
};

function decodeHtmlEntities(input: string): string {
    if (!input) return '';
    let s = input
        .replaceAll('&lt;', '<')
        .replaceAll('&gt;', '>')
        .replaceAll('&quot;', '"')
        .replaceAll('&apos;', "'")
        .replaceAll('&#39;', "'")
        .replaceAll('&nbsp;', ' ');
    s = s.replace(/&#(\d+);/g, (_, d: string) => String.fromCharCode(parseInt(d, 10)));
    s = s.replace(/&#x([0-9a-fA-F]+);/g, (_, h: string) => String.fromCharCode(parseInt(h, 16)));
    s = s.replaceAll('&amp;', '&');
    return s;
}

function toSafeHtmlText(input: string): string {
    const decoded = decodeHtmlEntities(input);
    return decoded.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
}

export function SearchTagsSidebar() {
    const [searchTags, setSearchTags] = useState<string>('');
    const { tagId, bookId } = useParams();
    const tags = useLiveQuery<Tag[]>(async () => {
        if (!searchTags) {
            return tagsDb.tags.toArray();
        }
        return tagsDb.tags.toArray().then(tags => {
            return tags.filter(tag => {
                return tag.id.toLowerCase().includes(searchTags.toLowerCase());
            });
        });
    }, [searchTags]);

    const [searchBooks, setSearchBooks] = useState<string>('');
    const books = useLiveQuery<Book[]>(async () => {
        if (!searchBooks) {
            return bookDb.books.toArray();
        }
        return bookDb.books.toArray().then(books => {
            return books.filter(book => {
                return book.bookName.toLowerCase().includes(searchBooks.toLowerCase());
            });
        });
    }, [searchBooks]);

    const [activeTab, setActiveTab] = useState<'tags' | 'books'>('tags');
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [advBookName, setAdvBookName] = useState<string>('');
    const [advId, setAdvId] = useState<string>('');
    const [advChapter, setAdvChapter] = useState<string>('');
    const [advResultSnippet, setAdvResultSnippet] = useState<string | null>(null);
    const [advLoading, setAdvLoading] = useState(false);
    const [advError, setAdvError] = useState<string | null>(null);
    const [advCopied, setAdvCopied] = useState(false);

    async function copyAdvDecoded() {
        if (!advResultSnippet) return;
        const decoded = decodeHtmlEntities(advResultSnippet);
        try {
            await navigator.clipboard.writeText(decoded);
            setAdvCopied(true);
            setTimeout(() => setAdvCopied(false), 1200);
        } catch {
            // ignore
        }
    }

    async function onAdvancedFind() {
        setAdvError(null);
        setAdvResultSnippet(null);
        if (!advBookName || !advId || !advChapter) return;

        try {
            setAdvLoading(true);
            const key = `${advBookName}:${advId}:${advChapter}`;
            const record = await tagsDb.snippetIndex.get(key);
            if (record && record.snippet) {
                setAdvResultSnippet(record.snippet);
            } else {
                setAdvError('No snippet found for that book, id, and chapter.');
            }
        } catch (err: any) {
            setAdvError(err?.message || String(err));
        } finally {
            setAdvLoading(false);
        }
    }

    function openAdvanced() {
        setAdvBookName('');
        setAdvId('');
        setAdvChapter('');
        setAdvResultSnippet(null);
        setAdvError(null);
        setAdvCopied(false);
        setAdvancedOpen(true);
    }

    if (!tags || !books) return <div>Loading...</div>;
    const sortedBooks = [...books].sort((a, b) => a.bookName.localeCompare(b.bookName));
    return (
        <div
            data-testid="search-tags-panel"
            className="bg-sidebar text-sidebar-foreground flex min-h-0 h-full max-h-full w-full shrink-0 flex-col overflow-hidden border-b md:w-[var(--sidebar-width)] md:border-b-0 md:border-r border-sidebar-border"
        >
            <SidebarHeader className="shrink-0 gap-3">
                <Tabs
                    value={activeTab}
                    onValueChange={(v: string) => setActiveTab(v === 'books' ? 'books' : 'tags')}
                    className="flex w-full min-w-0 flex-col gap-0"
                >
                    <div className="flex w-full min-w-0 flex-row flex-nowrap items-center gap-2">
                        <SidebarGroupLabel className="min-w-0 shrink truncate text-lg font-bold">
                            {activeTab === 'tags' ? 'Tags' : 'Books'}
                        </SidebarGroupLabel>
                        <span
                            className="min-w-[4px] flex-1 shrink"
                            aria-hidden
                        />
                        <TabsList className="ml-auto shrink-0">
                            <TabsTrigger value="tags">
                                <TagsIcon className="hover:text-primary" />
                            </TabsTrigger>
                            <TabsTrigger value="books">
                                <BookOpen className="hover:text-primary" />
                            </TabsTrigger>
                        </TabsList>
                    </div>
                    <TabsContent
                        value="tags"
                        className="m-0 hidden p-0"
                        tabIndex={-1}
                    />
                    <TabsContent
                        value="books"
                        className="m-0 hidden p-0"
                        tabIndex={-1}
                    />
                </Tabs>
                <SearchTags
                    search={activeTab === 'tags' ? searchTags : searchBooks}
                    setSearch={activeTab === 'tags' ? setSearchTags : setSearchBooks}
                    tabValue={activeTab}
                    onOpenAdvancedSearch={openAdvanced}
                />
            </SidebarHeader>
            <SidebarContent className="min-h-0 flex-1 overflow-hidden">
                <SidebarGroup className="min-h-0 flex flex-1 flex-col gap-2 overflow-hidden p-2 pb-3">
                    <SidebarGroupContent className="min-h-0 flex-1 overflow-y-auto [-webkit-overflow-scrolling:touch]">
                        <SidebarMenu>
                            {activeTab === 'tags'
                                ? tags.map(tag => (
                                      <SidebarMenuItem key={tag.id}>
                                          <SidebarMenuButton
                                              asChild
                                              isActive={tagId === tag.id}
                                          >
                                              <Link
                                                  to={`/search/tag/${encodeURIComponent(tag.id)}`}
                                                  className="flex justify-between"
                                              >
                                                  <span>{`<${tag.id}>`}</span>
                                                  <span className="text-sm text-gray-500">{tag.count}</span>
                                              </Link>
                                          </SidebarMenuButton>
                                      </SidebarMenuItem>
                                  ))
                                : books.map(book => (
                                      <SidebarMenuItem key={book.bookName}>
                                          <SidebarMenuButton
                                              asChild
                                              isActive={bookId === book.bookName}
                                          >
                                              <Link to={`/search/book/${encodeURIComponent(book.bookName)}`}>
                                                  <span>{book.bookName}</span>
                                              </Link>
                                          </SidebarMenuButton>
                                      </SidebarMenuItem>
                                  ))}
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
            </SidebarContent>
            <SidebarFooter className="h-12 shrink-0">
                <Dialog
                    open={advancedOpen}
                    onOpenChange={setAdvancedOpen}
                >
                    <DialogContent className="max-w-[80%] sm:max-w-[80%]">
                        <DialogHeader>
                            <DialogTitle>Advanced Snippet Search</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                            <div className="flex flex-col gap-1">
                                <label
                                    className="text-xs font-medium"
                                    htmlFor="adv-book"
                                >
                                    Book
                                </label>
                                <select
                                    id="adv-book"
                                    className="w-full rounded border px-2 py-1 text-sm"
                                    value={advBookName}
                                    onChange={e => setAdvBookName(e.target.value)}
                                    disabled={advLoading}
                                >
                                    <option value="">Select a book...</option>
                                    {sortedBooks.map(b => (
                                        <option
                                            key={b.bookName}
                                            value={b.bookName}
                                        >
                                            {b.bookName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex flex-col gap-1">
                                <label
                                    className="text-xs font-medium"
                                    htmlFor="adv-id"
                                >
                                    id
                                </label>
                                <input
                                    id="adv-id"
                                    className="rounded border px-2 py-1 text-sm"
                                    value={advId}
                                    onChange={e => setAdvId(e.target.value)}
                                    disabled={advLoading}
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <label
                                    className="text-xs font-medium"
                                    htmlFor="adv-chapter"
                                >
                                    zychapter
                                </label>
                                <input
                                    id="adv-chapter"
                                    className="rounded border px-2 py-1 text-sm"
                                    value={advChapter}
                                    onChange={e => setAdvChapter(e.target.value)}
                                    disabled={advLoading}
                                />
                            </div>
                            {advError && <div className="text-xs text-red-600">{advError}</div>}
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    onClick={onAdvancedFind}
                                    disabled={advLoading || !advBookName || !advId || !advChapter}
                                >
                                    {advLoading ? 'Finding...' : 'Find'}
                                </Button>
                                <DialogClose
                                    render={
                                        <Button
                                            type="button"
                                            variant="outline"
                                        />
                                    }
                                >
                                    Close
                                </DialogClose>
                            </div>
                            {advResultSnippet && (
                                <div className="mt-3">
                                    <div className="mb-2 flex items-center justify-between">
                                        <span className="text-muted-foreground text-xs">Snippet</span>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            onClick={copyAdvDecoded}
                                        >
                                            {advCopied ? 'Copied' : 'Copy'}
                                        </Button>
                                    </div>
                                    <div className="max-h-[50vh] overflow-auto rounded border bg-gray-50 p-2 text-xs">
                                        <pre className="whitespace-pre-wrap">
                                            <code
                                                dangerouslySetInnerHTML={{
                                                    __html: toSafeHtmlText(advResultSnippet),
                                                }}
                                            />
                                        </pre>
                                    </div>
                                </div>
                            )}
                        </div>
                        <DialogFooter />
                    </DialogContent>
                </Dialog>
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <Button
                            variant="outline"
                            className="gap-x-2"
                        >
                            <Trash2Icon
                                size={20}
                                color="red"
                            />
                            Clear Database
                        </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                            <AlertDialogDescription>
                                This action cannot be undone. It will delete the current database and load a fresh database from the seed
                                file.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={clearDatabase}>Continue</AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </SidebarFooter>
        </div>
    );
}
